import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import "../../styles/DocsManagement/docs_management.css";
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardSidebar from '../DashboardSideBar';
import Header from '../Header';
import Loading from '../Loading';
import FilePagination from '../Pagination.jsx';
import { NoFilesIllustration } from './ListFiles.jsx'
import ConfirmationModal from '../ConfirmationModal.jsx';
import {
    deleteFiles,
    deleteFile,
    handleBulkDownload,
    handleSecureDownload,
    sortFiles,
    previewFile
} from '../../utils/fileUtils.js';


const tusApi = process.env.REACT_APP_TUS_SERVER;
const FILE_STATUS = {
    CHECKING: 'checking',
    DONE: 'done',
    NOT_ACCEPTED: 'not_accepted',
    INFECTED: 'infected',
    PROCESSING: 'incomplete',
};

// Get file status based on logic
function getFileStatus({ inQuarantine, completed, infected }) {
    if (infected) {
        return FILE_STATUS.INFECTED;
    }

    if (completed) {
        if (inQuarantine) return FILE_STATUS.CHECKING;
        return FILE_STATUS.DONE;
    }

    return inQuarantine ? FILE_STATUS.CHECKING : FILE_STATUS.PROCESSING;
}

function FileStatus({ inQuarantine, completed, infected }) {
    const { t } = useTranslation();
    const normalizedStatus = getFileStatus({ inQuarantine, completed, infected });

    switch (normalizedStatus) {
        case FILE_STATUS.CHECKING:
            return <span className="file-status checking">{t('checking')}</span>;
        case FILE_STATUS.DONE:
            return <span className="file-status done">{t('done')}</span>;
        case FILE_STATUS.NOT_ACCEPTED:
            return <span className="file-status not-accepted">{t('not_accepted')}</span>;
        case FILE_STATUS.INFECTED:
            return <span className="file-status infected">{t('infected')}</span>;
        case FILE_STATUS.PROCESSING:
            return <span className="file-status processing">{t('incomplete')}</span>;
        default:
            return <span className="file-status unknown">{t('unknown')}</span>;
    }
}
export const fetchUploadedFiles = async (tusApi, username, token) => {
    try {
        const res = await fetch(`${tusApi}/api/uploads?userId=${encodeURIComponent(username)}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch uploaded files");
        return await res.json();
    } catch (err) {
        console.error("Error fetching uploaded files:", err);
        return []; // fallback
    }
};
export default function DocsManagement({ onDeleteSuccess, filesPerPage = 10 }) {
    const { user, token, loading } = useAuth();
    const username = user?.useName;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { lang } = useParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [files, setUploadedFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState();
    const [sortOrder, setSortOrder] = useState("asc");

    const [currentPage, setCurrentPage] = useState(1);
    const [currentItems, setCurrentItems] = useState([]);
    const [pageSize, setPageSize] = useState(filesPerPage);

    const [showModal, setShowModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);

    const filteredFiles = useMemo(() => {
        return files.filter(file =>
            file.fileName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [files, searchTerm]);

    // ✅ Calculate total pages after filtering
    const totalPages = Math.ceil(filteredFiles.length / pageSize);

    // ✅ Update current items when page or page size changes
    useEffect(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setCurrentItems(filteredFiles.slice(startIndex, endIndex));
    }, [currentPage, pageSize, filteredFiles]);

    // ✅ Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handlePageChange = (page, newPageSize = pageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(page);
    };
    const loadFiles = async () => {
        setFilesLoading(true)
        try {
            const res = await fetch(`${tusApi}/api/uploads/admin-stat`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Failed to fetch uploaded files');
            const files = await res.json();
            const completedFiles = files.filter(file => file.completed === true);

            setUploadedFiles(completedFiles);
        } catch (error) {
            console.error('Error loading files:', error.message);
        } finally {
            setFilesLoading(false);
        }
    };


    useEffect(() => {
        loadFiles();
    }, [username, token]);

    const formatFileSize = (size) => {
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };


    const handleSortFiles = (key) => {
        const { sorted, newSortOrder } = sortFiles(files, key, sortOrder);
        setUploadedFiles(sorted);
        setSortOrder(newSortOrder);
    };


    const toggleFileSelection = (fileId) => {
        setSelectedFiles((prevSelected) =>
            prevSelected.includes(fileId)
                ? prevSelected.filter((id) => id !== fileId)
                : [...prevSelected, fileId]
        );
    };

    // Delete a single file
    const handleDeleteFile = async (file) => {
        if (!file) return;

        // if (!window.confirm(`Are you sure you want to delete "${file.fileName}"?`)) {
        //     return;
        // }

        setFilesLoading(true);
        try {
            await deleteFile(file, tusApi, token);
            await loadFiles();
            if (typeof onDeleteSuccess === "function") onDeleteSuccess();
        } catch (err) {
            console.error(`Failed to delete file ${file.fileName}:`, err);
        } finally {
            setFilesLoading(false);
        }
    };

    // Delete selected files
    const handleDeleteSelectedFiles = async () => {
        if (!selectedFiles.length) return console.log("No files selected.");

        setFilesLoading(true);
        try {
            // if (!window.confirm(`Delete ${selectedFiles.length} selected file(s)?`)) {
            //     return setFilesLoading(false);
            // }

            const filesToDelete = selectedFiles
                .map(fileId => files.find(f => f.fileId === fileId))
                .filter(Boolean);

            await deleteFiles(filesToDelete, tusApi, token, loadFiles, onDeleteSuccess);
            setSelectedFiles([]);
        } catch (err) {
            console.error("Bulk delete error:", err);
        } finally {
            setFilesLoading(false);
        }
    };

    // Modal confirm button
    const handleConfirmDelete = () => {
        if (!fileToDelete) return;

        if (Array.isArray(fileToDelete)) {
            handleDeleteSelectedFiles(); // bulk delete
        } else {
            handleDeleteFile(fileToDelete); // single delete
        }
        closeModal();
    };

    // Open modal for single file
    const openDeleteModal = (file) => {
        setFileToDelete(file);
        setShowModal(true);
    };

    // Open modal for bulk delete
    const openDeleteSelectedModal = () => {
        if (!selectedFiles.length) return;
        setFileToDelete(selectedFiles); // array of fileIds
        setShowModal(true);
    };

    // Close modal
    const closeModal = () => {
        setShowModal(false);
        setFileToDelete(null);
    };
    if (loading || filesLoading) return <Loading />;

    const totalSizeBytes = files.reduce((sum, file) => sum + file.fileSize, 0);
    const totalSizeMB = totalSizeBytes / (1024 * 1024);
    const maxSize = 10;

    if (loading || filesLoading) return <Loading />;
    return (
        <div className="uploadpage">
            <Header hideHamburger={false} profileHide={true} hideNav={true} />
            <div className="dashboard">
                <DashboardSidebar
                    uploadedFiles={files}
                    totalSizeMB={totalSizeMB}
                    maxSize={maxSize}
                />
                <div className="doc_manager">
                    <div className="container">
                        <div className="head">
                            <h2>{t('doc')}</h2>
                            <p>{t('doc-management-description')} </p>
                        </div>
                        <div className="contents">
                            <div className="content">
                                <div className="searches">
                                    <div className="search">
                                        <input
                                            type="text"
                                            placeholder={t('search-doc')}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button className="search-button">
                                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#9ca3af">
                                                <path d="M380-320q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l224 224q11 11 11 28t-11 28q-11 11-28 11t-28-11L532-372q-30 24-69 38t-83 14Zm0-80q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
                                            </svg>
                                        </button>
                                    </div>
                                    {selectedFiles.length > 0 && (

                                        <div className="bulk-actions">

                                            <button
                                                className='delete-btn'
                                                type='button'
                                                onClick={openDeleteSelectedModal}
                                                disabled={filesLoading}
                                            >
                                                {t('del-selected')}
                                            </button>

                                            <button
                                                className='download-btn'
                                                type='button'
                                                onClick={() => handleBulkDownload(selectedFiles, files, tusApi, token)}
                                                disabled={filesLoading}
                                            >
                                                {t('down-selected')}
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="add-new"
                                        onClick={() => navigate(`/lang/${lang}/upload`)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#e3e3e3"><path d="M440-440H240q-17 0-28.5-11.5T200-480q0-17 11.5-28.5T240-520h200v-200q0-17 11.5-28.5T480-760q17 0 28.5 11.5T520-720v200h200q17 0 28.5 11.5T760-480q0 17-11.5 28.5T720-440H520v200q0 17-11.5 28.5T480-200q-17 0-28.5-11.5T440-240v-200Z" /></svg>
                                        {t('upload-file')}
                                    </button>

                                </div>
                                {files.length === 0 ? (
                                    <div className="non-data">
                                        <div className="img-head">
                                            {NoFilesIllustration({ width: "150", height: "140" })}
                                        </div>
                                        <div className="non-content">
                                            <h4 >
                                                {t('doc-content')}
                                            </h4>
                                            <p>    {t('doc-content-description')}</p>
                                        </div>
                                    </div>

                                ) : (

                                    <>
                                        <div className="file-table-wrapper">
                                            <table className="file-table">
                                                <thead>
                                                    <tr>
                                                        <th>
                                                            <div className="file-name">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedFiles.length === files.length && filteredFiles.length > 0}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedFiles(filteredFiles.map(file => file.fileId));
                                                                        } else {
                                                                            setSelectedFiles([]);
                                                                        }
                                                                    }}
                                                                />
                                                                <div className="head-file" onClick={() => handleSortFiles("fileName")} style={{ cursor: "pointer" }}>
                                                                    <span>{t('file-name')}</span>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        height="20"
                                                                        viewBox="0 -960 960 960"
                                                                        width="20"
                                                                        fill="#9ca3af"
                                                                    >
                                                                        <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                    </svg>
                                                                </div>

                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="file-name">
                                                                <div className="head-file" onClick={() => handleSortFiles("fileDate")} style={{ cursor: "pointer" }}>

                                                                    <span>{t('uploaded-date')}</span>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        height="20"
                                                                        viewBox="0 -960 960 960"
                                                                        width="20"
                                                                        fill="#9ca3af"
                                                                    >
                                                                        <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                    </svg>
                                                                </div>

                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="file-name">
                                                                <div className="head-file" onClick={() => handleSortFiles("fileSize")} style={{ cursor: "pointer" }}>
                                                                    <span>{t('file-size')}</span>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        height="20"
                                                                        viewBox="0 -960 960 960"
                                                                        width="20"
                                                                        fill="#9ca3af"
                                                                    >
                                                                        <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                    </svg>
                                                                </div>

                                                            </div>
                                                        </th>
                                                        <th>
                                                            <div className="file-name">
                                                                <div className="head-file" onClick={() => handleSortFiles("username")} style={{ cursor: "pointer" }}>
                                                                    <span>{t('owner')}</span>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        height="20"
                                                                        viewBox="0 -960 960 960"
                                                                        width="20"
                                                                        fill="#9ca3af"
                                                                    >
                                                                        <path d="M360-440q-17 0-28.5-11.5T320-480v-247l-75 75q-11 11-27.5 11T189-652q-12-12-12-28.5t12-28.5l143-143q6-6 13-8.5t15-2.5q8 0 15 2.5t13 8.5l144 144q12 12 11.5 28T531-652q-12 11-28 11.5T475-652l-75-75v247q0 17-11.5 28.5T360-440ZM600-97q-8 0-15-2.5t-13-8.5L428-252q-12-12-11.5-28t12.5-28q12-11 28-11.5t28 11.5l75 75v-247q0-17 11.5-28.5T600-520q17 0 28.5 11.5T640-480v247l75-75q11-11 27.5-11t28.5 11q12 12 12 28.5T771-251L628-108q-6 6-13 8.5T600-97Z" />
                                                                    </svg>
                                                                </div>

                                                            </div>
                                                        </th>

                                                        <th>{t('status')}</th>
                                                        <th>{t('action')}</th>
                                                    </tr>

                                                </thead>
                                                <tbody>
                                                    {currentItems.map((file) => (
                                                        <tr key={file.fileId}>
                                                            <td>
                                                                <div className="file-name">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedFiles.includes(file.fileId)}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleFileSelection(file.fileId);
                                                                        }}
                                                                    />

                                                                    <span
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            previewFile(file.fileId, token, tusApi);
                                                                        }}
                                                                    >
                                                                        {file.fileName}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>{file.fileDate}</td>
                                                            <td>{formatFileSize(file.fileSize)}</td>
                                                            <td>{file.username}</td>
                                                            <td className="status">
                                                                <FileStatus inQuarantine={file.quarantine} completed={file.completed} infected={file.infected} />
                                                            </td>
                                                            <td className="actions">
                                                                {/* <button className="restart-button" title="restart" >
                                                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#9ca3af"><path d="M440-122q-121-15-200.5-105.5T160-440q0-66 26-126.5T260-672l57 57q-38 34-57.5 79T240-440q0 88 56 155.5T440-202v80Zm80 0v-80q87-16 143.5-83T720-440q0-100-70-170t-170-70h-3l44 44-56 56-140-140 140-140 56 56-44 44h3q134 0 227 93t93 227q0 121-79.5 211.5T520-122Z" /></svg>
                                                    </button> */}
                                                                <button className="download-button" title="Download" onClick={() => handleSecureDownload(file.username, file.fileId, file.fileName, tusApi, token)}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M12 15.248q-.161 0-.298-.053t-.267-.184l-2.62-2.619q-.146-.146-.152-.344t.152-.363q.166-.166.357-.169q.192-.003.357.163L11.5 13.65V5.5q0-.213.143-.357T12 5t.357.143t.143.357v8.15l1.971-1.971q.146-.146.347-.153t.366.159q.16.165.163.354t-.162.353l-2.62 2.62q-.13.13-.267.183q-.136.053-.298.053M6.616 19q-.691 0-1.153-.462T5 17.384v-1.923q0-.213.143-.356t.357-.144t.357.144t.143.356v1.923q0 .231.192.424t.423.192h10.77q.23 0 .423-.192t.192-.424v-1.923q0-.213.143-.356t.357-.144t.357.144t.143.356v1.923q0 .691-.462 1.153T17.384 19z" /></svg>
                                                                </button>


                                                                <button
                                                                    className="delete-button"
                                                                    title="Delete"
                                                                    onClick={() => {
                                                                        openDeleteModal(file)

                                                                    }}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24"><path fill="currentColor" d="M7.616 20q-.672 0-1.144-.472T6 18.385V6H5V5h4v-.77h6V5h4v1h-1v12.385q0 .69-.462 1.153T16.384 20zM17 6H7v12.385q0 .269.173.442t.443.173h8.769q.23 0 .423-.192t.192-.424zM9.808 17h1V8h-1zm3.384 0h1V8h-1zM7 6v13z" /></svg>

                                                                </button>
                                                            </td>


                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <FilePagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            totalItems={files?.length}
                                            onPageChange={handlePageChange}
                                        />
                                    </>
                                )}
                                <ConfirmationModal
                                    isOpen={showModal}
                                    onClose={closeModal}
                                    onConfirm={handleConfirmDelete}
                                    header={
                                        Array.isArray(fileToDelete)
                                            ? `${t("head-del-file")} ${fileToDelete.length} ${t("files")}?`
                                            : `${t("head-del-file")} ${fileToDelete?.fileName} ?`
                                    }
                                    message={t("paragraph-del-file")}
                                />

                            </div>
                        </div>
                    </div>
                </div></div></div >

    )
}